import {
  ApiAction,
  ApiSubject,
  ApiUser,
  ApiUserOrgRole,
  ApiUserPermission,
  ApiUserPermissions,
  ApiUserSchema,
  AppAbility,
  createAbilityFromPermissions,
  KaiProApiClient,
  userCan as userCanCheck,
  validateUserCan as validateUserCanCheck,
} from '@kaiizen/kai-pro-api';
import { optional, optionalBoolean, optionalNumber, optionalString, zodLess, ZodLessInfer } from '@kaiizen/zod-less';
import { ErrorWithStatus } from '@kaiizen/zod-less-api';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useKaiProApiState, usePersistentAuthSession } from './contexts';

const AuthCredentialsSchema = zodLess({
  token: optionalString,
  sessionId: optionalString,
  status: optionalString,
  authCode: optionalString,
  messageUrl: optionalString,
  verificationToken: optionalString,
  waitingForAuthCompletion: optionalBoolean,
  startedWaitingForAuthCompletionAt: optionalNumber,
  error: optionalString,
  phoneNumber: optionalString,
  email: optionalString,
  user: optional(ApiUserSchema),
  orgId: optionalString,
  // Super admin impersonation backup: set when impersonating so we can restore the original session.
  impersonationBackupToken: optionalString,
  impersonationBackupUser: optional(ApiUserSchema),
  impersonationBackupOrgId: optionalString,
});

export type AuthCredentials = ZodLessInfer<typeof AuthCredentialsSchema>;

type AuthSendCodeChannel = 'sms' | 'whatsapp';

type SwitchOrgOptions = {
  userId?: string;
  preserveOriginalSession?: boolean;
};

export type AuthCredentialsStateMutators = {
  setToken: (token: string) => Promise<void>;
  clearToken: () => Promise<void>;
  initiateReceiveSmsAuth: (phoneNumber: string, messageType: 'sms' | 'whatsapp') => Promise<void>;
  switchOrg: (orgId: string, options?: SwitchOrgOptions) => Promise<void>;
  restoreOriginalUser: () => Promise<void>;
  initiateSendSmsAuth: (phoneNumber: string, messageType?: AuthSendCodeChannel) => Promise<void>;
  initiateSendEmailAuth: (email: string) => Promise<void>;
  validateSendSmsAuth: (phoneNumber: string, smsCode: string, messageType?: AuthSendCodeChannel) => Promise<void>;
  validateSendEmailAuth: (email: string, smsCode: string) => Promise<void>;
  getAuthQrCode: (
    { sessionId, phoneNumber, verificationToken, messageType }: {
      sessionId: string;
      phoneNumber: string;
      verificationToken: string;
      messageType: 'sms' | 'whatsapp';
    },
  ) => Promise<string>;
};

type AuthContextType = {
  credentials: AuthCredentials;
  credentialsMutators: AuthCredentialsStateMutators;
  token?: string;
  sessionId?: string;
  user?: ApiUser;
  userId?: string;
  orgId?: string;
  orgName?: string;
  role?: ApiUserOrgRole;
  permissions?: ApiUserPermission[];
  isSuperAdmin?: boolean;
  userPermissions?: ApiUserPermissions;
  initializing: boolean;
  withAuthErrorHandling: <T>(fn: () => Promise<T>) => Promise<T>;
  handleAuthError: (error: any) => void;
  userCan: (action: ApiAction, subject: ApiSubject) => boolean;
  validateUserCan: (action: ApiAction, subject: ApiSubject) => void;
};

export const AuthContext = createContext<AuthContextType | null>(null);

const POLL_INTERVAL = 1000; // 1 second
const MAX_POLL_TIME = 5 * 60 * 1000; // 5 minutes

const getSessionIdFromToken = (token?: string): string | undefined => {
  if (!token) return undefined;
  const tokenParts = token.split('.');
  if (tokenParts.length < 2) return undefined;
  try {
    const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const nodeBuffer = (globalThis as { Buffer?: { from(s: string, enc: string): { toString(enc: string): string } } }).Buffer;
    const decodedPayload = typeof globalThis.atob === 'function'
      ? globalThis.atob(paddedBase64)
      : nodeBuffer
      ? nodeBuffer.from(paddedBase64, 'base64').toString('utf8')
      : undefined;
    if (!decodedPayload) return undefined;
    const payload = JSON.parse(decodedPayload) as { sessionId?: string };
    return typeof payload.sessionId === 'string' ? payload.sessionId : undefined;
  } catch {
    return undefined;
  }
};

const withResolvedSessionId = (credentials: AuthCredentials): AuthCredentials => ({
  ...credentials,
  sessionId: credentials.sessionId ?? getSessionIdFromToken(credentials.token),
});

const shouldPreserveOriginalSession = (options?: SwitchOrgOptions) => !!options?.userId || !!options?.preserveOriginalSession;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { credentials: persistedCredentials, isInitialized, saveAuthCredentials, ...StandardReactQueryStatusInfo } = usePersistentAuthSession();
  const [credentials, setCredentials] = useState<AuthCredentials>({});
  const [userPermissions, setUserPermissions] = useState<ApiUserPermissions | undefined>();
  const [initializing, setInitializing] = useState(true);
  const credentialsRef = useRef(credentials);

  // Update ref when credentials change
  useEffect(() => {
    credentialsRef.current = credentials;
  }, [credentials]);

  // Sync persisted credentials to local state
  useEffect(() => {
    if (isInitialized) {
      setCredentials(withResolvedSessionId(persistedCredentials || {}));
      setInitializing(false);
    }
  }, [persistedCredentials, isInitialized]);

  const clearToken = useCallback(async () => {
    setCredentials({});
    await saveAuthCredentials({});
  }, [saveAuthCredentials]);

  const setAndSaveCredentials = useCallback(async (credentials: AuthCredentials) => {
    const resolvedCredentials = withResolvedSessionId(credentials);
    setCredentials(resolvedCredentials);
    await saveAuthCredentials(resolvedCredentials);
  }, [saveAuthCredentials]);

  // *************************************************************
  //  SHARED ERROR HANDLING
  // *************************************************************

  const handleAuthError = useCallback((error: any, info?: any) => {
    if (error instanceof ErrorWithStatus && (error.status === 401)) {
      console.warn('Auth Manager: logging out due to invalid/expired/deactivated/unauthorized credentials', error, info);
      clearToken();
    }
    throw error;
  }, [clearToken]);

  const withAuthErrorHandling = useCallback(<T>(fn: () => Promise<T>, info?: any): Promise<T> => {
    return fn().catch(error => handleAuthError(error, info));
  }, [handleAuthError]);

  // *************************************************************
  //  API CLIENT
  // *************************************************************
  const { apiUrl } = useKaiProApiState();
  // note we can't use useKaiProApiClient here because it would cause a circular dependency
  const kaiProApiClient = useMemo(() => new KaiProApiClient({ token: credentials?.token, errorHandler: handleAuthError, apiUrl }), [
    credentials?.token,
    handleAuthError,
    apiUrl,
  ]);

  // *************************************************************
  //  USER PERMISSIONS (refetch when token or userId changes)
  // *************************************************************
  useEffect(() => {
    const token = credentials?.token;
    const userId = credentials?.user?.id;
    if (!token || !userId) {
      setUserPermissions(undefined);
      return;
    }
    kaiProApiClient.getUserPermissions(userId)
      .then(setUserPermissions)
      .catch(() => setUserPermissions(undefined));
  }, [credentials?.token, credentials?.user?.id, kaiProApiClient]);

  // *************************************************************
  //  ACCESS CONTROL
  // *************************************************************
  const ability = useMemo<AppAbility | undefined>(
    () => userPermissions?.permissions ? createAbilityFromPermissions(userPermissions.permissions) : undefined,
    [userPermissions?.permissions],
  );

  const userCan = useCallback(
    (action: ApiAction, subject: ApiSubject): boolean => userCanCheck(ability, action, subject),
    [ability],
  );

  const validateUserCan = useCallback(
    (action: ApiAction, subject: ApiSubject): void => validateUserCanCheck(ability, action, subject),
    [ability],
  );

  // *************************************************************
  // Receive-SMS AUTH INITIATION
  // *************************************************************

  const checkReceiveSmsAuth = useCallback(async () => {
    const credentials = credentialsRef.current;
    if (!credentials.sessionId || !credentials.verificationToken || !credentials.phoneNumber) return;
    try {
      console.log('Auth check: checking session', { credentials });
      const result = await kaiProApiClient.checkReceiveSmsAuth(credentials.sessionId, credentials.phoneNumber, credentials.verificationToken);
      if (result.status === 'authorized' && result.token) {
        const newCredentials = { token: result.token, sessionId: result.sessionId, user: result.user, orgId: result.orgId };
        await setAndSaveCredentials(newCredentials);
        console.log('Auth check: SUCCESS');
        return true;
      } else if (result.status === 'invalid' || result.status === 'deactivated') {
        const updatedCredentials = {
          ...credentialsRef.current,
          waitingForAuthCompletion: false,
          startedWaitingForAuthCompletionAt: undefined,
          error: 'Session expired. Please try again.',
        };
        await setAndSaveCredentials(updatedCredentials);
        console.log('Auth check: INVALID/DEACTIVATED');
        return false;
      } else {
        console.log('Auth check: still pending');
        return false;
      }
    } catch (error) {
      console.log('Auth check: ERROR', error);
      return false;
    }
  }, []);

  // Polling logic using useEffect
  useEffect(() => {
    setInterval(async () => {
      const credentials = credentialsRef.current;
      if (credentials.waitingForAuthCompletion) {
        if (credentials.startedWaitingForAuthCompletionAt && Date.now() - credentials.startedWaitingForAuthCompletionAt > MAX_POLL_TIME) {
          console.log('Auth polling: TIMEOUT');
          await setAndSaveCredentials({});
          return;
        }

        await checkReceiveSmsAuth();
      }
    }, POLL_INTERVAL);
  }, []);

  const initiateReceiveSmsAuth = useCallback(async (phoneNumber: string, messageType: 'sms' | 'whatsapp') => {
    try {
      const response = await kaiProApiClient.initiateReceiveSmsAuth(phoneNumber, messageType);
      console.log('initiateReceiveSmsAuth: RESPONSE', response);
      const newCredentials = {
        ...response,
        waitingForAuthCompletion: true,
        startedWaitingForAuthCompletionAt: Date.now(),
        phoneNumber,
      };
      await setAndSaveCredentials(newCredentials);
    } catch (error) {
      console.log('initiateReceiveSmsAuth: ERROR', error);
      const friendlyMessage = error instanceof ErrorWithStatus && error.status === 401
        ? 'This phone number is not authorized. Please contact help@kai.pro for onboarding support or your company administrator.'
        : 'Failed to initiate authentication. Please try again.';
      const errorCredentials = {
        ...credentialsRef.current,
        error: friendlyMessage,
      };
      await setAndSaveCredentials(errorCredentials);
    }
  }, [setAndSaveCredentials, kaiProApiClient]);

  const getAuthQrCode = useCallback(
    async (
      { sessionId, phoneNumber, verificationToken, messageType }: {
        sessionId: string;
        phoneNumber: string;
        verificationToken: string;
        messageType: 'sms' | 'whatsapp';
      },
    ) => {
      const response = await kaiProApiClient.getQrCode(sessionId, phoneNumber, verificationToken, messageType);
      return response.qrCodeUrl;
    },
    [kaiProApiClient],
  );

  // *************************************************************
  // Send-SMS AUTH INITIATION
  // *************************************************************

  const initiateSendSmsAuth = useCallback(async (phoneNumber: string, messageType?: AuthSendCodeChannel) => {
    await kaiProApiClient.initiateSendSmsAuth({ phoneNumber, messageType: messageType || 'sms' });
    await setAndSaveCredentials({
      waitingForAuthCompletion: true,
      phoneNumber,
      email: undefined,
    });
  }, [kaiProApiClient, setAndSaveCredentials]);

  const initiateSendEmailAuth = useCallback(async (email: string) => {
    await kaiProApiClient.initiateSendEmailAuth(email);
    await setAndSaveCredentials({
      waitingForAuthCompletion: true,
      email,
      phoneNumber: undefined,
    });
  }, [kaiProApiClient, setAndSaveCredentials]);

  const validateSendSmsAuth = useCallback(async (phoneNumber: string, smsCode: string, messageType?: AuthSendCodeChannel) => {
    const response = await kaiProApiClient.validateSendSmsAuth({ phoneNumber, code: smsCode });

    await setAndSaveCredentials({
      token: response.token,
      user: response.user,
      orgId: response.orgId,
      waitingForAuthCompletion: false,
      phoneNumber,
      email: undefined,
    });
  }, [setAndSaveCredentials, kaiProApiClient]);

  const validateSendEmailAuth = useCallback(async (email: string, smsCode: string) => {
    const response = await kaiProApiClient.validateSendEmailAuth(email, smsCode);

    await setAndSaveCredentials({
      token: response.token,
      user: response.user,
      orgId: response.orgId,
      waitingForAuthCompletion: false,
      email,
      phoneNumber: undefined,
    });
  }, [setAndSaveCredentials, kaiProApiClient]);

  // *************************************************************
  //  ORG SWITCHING
  // *************************************************************

  const switchOrg = useCallback(async (orgId: string, options?: SwitchOrgOptions) => {
    const credentials = credentialsRef.current;
    if (!credentials.token) return;
    try {
      const result = await kaiProApiClient.switchOrg(orgId, { userId: options?.userId });
      const newCredentials: AuthCredentials = { ...credentials, token: result.token, sessionId: getSessionIdFromToken(result.token), orgId };
      if (shouldPreserveOriginalSession(options)) {
        if (!credentials.impersonationBackupToken) {
          newCredentials.impersonationBackupToken = credentials.token;
          newCredentials.impersonationBackupUser = credentials.user;
          newCredentials.impersonationBackupOrgId = credentials.orgId;
        }
      }
      if (options?.userId) {
        const clientWithNewToken = new KaiProApiClient({ token: result.token, errorHandler: handleAuthError, apiUrl });
        const user = await clientWithNewToken.getUser(options.userId);
        newCredentials.user = user;
      }
      await setAndSaveCredentials(newCredentials);
    } catch (error) {
      console.error('switchOrg: ERROR', error);
      throw error;
    }
  }, [setAndSaveCredentials, kaiProApiClient, handleAuthError, apiUrl]);

  const restoreOriginalUser = useCallback(async () => {
    const credentials = credentialsRef.current;
    if (!credentials.impersonationBackupToken) return;
    await setAndSaveCredentials({
      ...credentials,
      token: credentials.impersonationBackupToken,
      sessionId: getSessionIdFromToken(credentials.impersonationBackupToken),
      user: credentials.impersonationBackupUser,
      orgId: credentials.impersonationBackupOrgId,
      impersonationBackupToken: undefined,
      impersonationBackupUser: undefined,
      impersonationBackupOrgId: undefined,
    });
  }, [setAndSaveCredentials]);

  // *************************************************************
  //  CONTEXT
  // *************************************************************

  const value: AuthContextType = {
    credentials,
    token: credentials?.token,
    sessionId: credentials?.sessionId,
    user: credentials?.user,
    userId: credentials?.user?.id,
    orgId: userPermissions?.orgId ?? credentials?.orgId,
    orgName: userPermissions?.orgName,
    role: userPermissions?.role,
    permissions: userPermissions?.permissions,
    isSuperAdmin: userPermissions?.isSuperAdmin,
    userPermissions,
    initializing,
    credentialsMutators: {
      setToken: async (token: string) => {
        const newCredentials = { token };
        await setAndSaveCredentials(newCredentials);
      },
      clearToken,
      initiateReceiveSmsAuth,
      switchOrg,
      restoreOriginalUser,
      initiateSendSmsAuth,
      initiateSendEmailAuth,
      validateSendSmsAuth,
      validateSendEmailAuth,
      getAuthQrCode,
    },
    withAuthErrorHandling,
    handleAuthError,
    userCan,
    validateUserCan,
    ...StandardReactQueryStatusInfo,
  };

  return React.createElement(AuthContext.Provider, { value }, children);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
